// Meta Pixel helper — loads the pixel with a head-safe injection that works even
// in module-based preview environments where the document has no <script> tags.
// (The official snippet / react-facebook-pixel package crash there because they
// rely on document.getElementsByTagName('script')[0].parentNode.)

const PIXEL_ID = '1202190732067002'

declare global {
  interface Window {
    fbq?: any
    _fbq?: any
  }
}

let initialized = false

// Resolves once fbevents.js has actually finished loading, so queued events
// are guaranteed to flush before we navigate away.
let readyResolve: (() => void) | null = null
const pixelReady: Promise<void> = new Promise((resolve) => {
  readyResolve = resolve
})

function loadFbevents(): void {
  // Already loaded.
  if (window.fbq) {
    readyResolve?.()
    return
  }

  const n: any = (window.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
  })
  if (!window._fbq) window._fbq = n
  n.push = n
  n.loaded = true
  n.version = '2.0'
  n.queue = []

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  // Resolve readiness once the real library is loaded (it auto-flushes the
  // queued PageView / AddToCart calls at this point).
  script.onload = () => readyResolve?.()
  script.onerror = () => readyResolve?.() // never hang if it's blocked

  // Head-safe insertion: prefer an existing <script>, otherwise fall back to
  // <head> / <body> / <html>. This avoids the `parentNode` crash entirely.
  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    ;(document.head || document.body || document.documentElement).appendChild(
      script,
    )
  }
}

/**
 * Resolves when the pixel library is loaded (or immediately if it was blocked
 * after a short grace period). Use this to wait before navigating away so the
 * AddToCart request has a chance to actually be sent.
 */
export function whenPixelReady(timeoutMs = 1200): Promise<void> {
  return Promise.race([
    pixelReady,
    new Promise<void>((resolve) => window.setTimeout(resolve, timeoutMs)),
  ])
}

/**
 * Initializes the Meta Pixel and fires a PageView.
 * Safe to call multiple times — it only initializes once.
 */
export function initMetaPixel(): void {
  if (typeof window === 'undefined' || initialized) return
  initialized = true

  loadFbevents()
  window.fbq('init', PIXEL_ID)
  window.fbq('track', 'PageView')
}

/** Generates a unique event id so the fbq event and the beacon dedupe to one. */
function newEventId(): string {
  return (
    'atc-' +
    Date.now().toString(36) +
    '-' +
    Math.random().toString(36).slice(2, 10)
  )
}

/**
 * Fires AddToCart in a way that survives an immediate same-tab redirect.
 *
 * 1. window.fbq('track','AddToCart', ..., { eventID }) — normal pixel event.
 * 2. A `navigator.sendBeacon` (with an <img> fallback) directly to Meta's
 *    /tr endpoint. sendBeacon is guaranteed by the browser to be delivered
 *    even while the page is unloading/navigating, so Meta receives the event
 *    even if we redirect right away. Both carry the same eventID, so Meta
 *    deduplicates them into a single AddToCart.
 *
 * Returns the eventID that was sent (useful for logging / debugging).
 */
export function trackAddToCart(): string {
  if (typeof window === 'undefined') return ''

  const eventID = newEventId()

  // 1) Standard pixel event (best for attribution when the page stays alive).
  if (window.fbq) {
    window.fbq('track', 'AddToCart', {}, { eventID })
  }

  // 2) Guaranteed-delivery beacon that survives navigation.
  const params = new URLSearchParams({
    id: PIXEL_ID,
    ev: 'AddToCart',
    eid: eventID,
    dl: window.location.href,
    rl: document.referrer || '',
    if: 'false',
    ts: Date.now().toString(),
    noscript: '1',
  })
  const beaconUrl = `https://www.facebook.com/tr/?${params.toString()}`

  let beaconSent = false
  if (typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
    try {
      beaconSent = navigator.sendBeacon(beaconUrl)
    } catch {
      beaconSent = false
    }
  }
  if (!beaconSent) {
    // Fallback: fire-and-forget image request.
    const img = new Image()
    img.src = beaconUrl
  }

  // Lightweight confirmation you can see in the browser console when testing.
  // eslint-disable-next-line no-console
  console.info(
    `[MetaPixel] AddToCart sent (eventID: ${eventID}, beacon: ${
      beaconSent ? 'sendBeacon' : 'image-fallback'
    })`,
  )

  return eventID
}
